import DOMPurify from "dompurify";

/**
 * ST143-TI-17771 (§3): thin wrapper around DOMPurify used as defense in depth for every "KEEP + SANITIZE" v-html
 * binding (Tier c) and for the SweetAlert2 `html: true` call sites. The backend is the authoritative
 * sanitize-on-write pipeline (see lots A-D) — this service does not replace it, it protects the front against
 * gaps in that guarantee (in-progress lots, unverified paths, future regressions).
 *
 * Two allowlist profiles:
 * - "richText": back-office / TinyMCE WYSIWYG content (formatting, lists, links, images, tables).
 * - "mail": rendered mail bodies, slightly more permissive (adds table layout attributes commonly used by
 *   transactional email HTML).
 */
class HtmlSanitizer {
  RICH_TEXT_PROFILE = "richText";
  MAIL_PROFILE = "mail";

  ALLOWED_TAGS_RICH_TEXT = [
    "p",
    "br",
    "strong",
    "b",
    "em",
    "i",
    "u",
    "s",
    "span",
    "div",
    "ul",
    "ol",
    "li",
    "a",
    "img",
    "table",
    "thead",
    "tbody",
    "tfoot",
    "tr",
    "th",
    "td",
    "h1",
    "h2",
    "h3",
    "h4",
    "h5",
    "h6",
    "blockquote",
    "hr",
    "sub",
    "sup",
    // MR review finding (ST143-TI-17771, MR !1586, Thibault Chardigny): the `codesample` TinyMCE plugin is
    // active (src/components/form/EditorTypeComponent.vue) and emits <pre><code>...</code></pre>. Neither tag
    // was allowlisted, so existing content using it silently lost its code block on first v-safe-html render.
    // Added `pre`/`code` — plain text-rendering tags with no attribute-driven risk (see the scoped `class`
    // handling below for the language-* highlighting hint, the only attribute this plugin actually needs).
    // Deliberately NOT added: `video`/`iframe` (the `media` plugin) and `nav` (the `toc` plugin, which also
    // needs `id` to jump to headings — `id` stays stripped for DOM-clobbering safety). Both would reopen
    // exactly the tag classes FORBID_TAGS/ALLOWED_ATTR were built to exclude; accepted as a documented content
    // loss rather than widened without a dedicated safeguard for each.
    "pre",
    "code",
    "mark",
  ];

  // Security-review finding (ST143-TI-17771, BLOCKING): `class` was allowlisted here, and the app loads the
  // full Bootstrap utility CSS globally (src/assets/sass/app.scss). A single class like "modal-backdrop" or
  // "position-fixed top-0 start-0 vw-100 vh-100" reproduces the exact full-viewport UI-redress overlay the
  // `style` allowlist below was built to prevent — with no CSS parsing involved at all, making the style fix
  // moot. Verified: TinyMCE's table/list/image plugins (the only editor content this profile needs to
  // preserve) emit inline `style`, not framework classes — grep of the editor config and every existing
  // sanitizer/component test confirms nothing relies on `class` surviving sanitization. Removed rather than
  // attempting a class-name allowlist: a denylist of "dangerous" utility classes would repeat the exact
  // structural mistake the style fix corrected (Bootstrap adds utilities every minor version), and a positive
  // allowlist of literal class names has no real content to allow in the first place.
  // MR review finding (ST143-TI-17771, MR !1586, Thibault Chardigny): width/height are non-executable
  // dimension attributes (not URL/script-bearing), missing here despite being written by TinyMCE's `image`
  // plugin on resize (existing resized images silently lost their explicit size). Already allowed in
  // MAIL_PROFILE below; added here too.
  //
  // `class` IS listed here again (DOMPurify's ALLOWED_ATTR is not per-tag, so there is no way to allow it only
  // on <code>) — but see the afterSanitizeAttributes hook below, which strips it back out on every element
  // except <code>, and even there keeps only tokens matching SAFE_CODE_CLASS_PATTERN. The BLOCKING finding
  // that removed `class` entirely (full-viewport overlay via the app's global Bootstrap utility CSS) is not
  // reopened: no Bootstrap utility name can match "language-" followed by identifier characters, and every
  // other tag loses `class` exactly as before.
  ALLOWED_ATTR_RICH_TEXT = [
    "href",
    "src",
    "alt",
    "title",
    "style",
    "target",
    "rel",
    "colspan",
    "rowspan",
    "width",
    "height",
    "class",
  ];

  // MR review finding (ST143-TI-17771, MR !1586, Thibault Chardigny): the `codesample` plugin's syntax
  // highlighting hint is a `class="language-xxx"` token on `<code>`. Scoped allowlist instead of reopening
  // `class` everywhere: only this exact token shape survives, only on `<code>`, enforced token-by-token (not
  // a whole-value regex test) so a payload cannot smuggle an extra Bootstrap utility class alongside a
  // legitimate language token (e.g. class="language-js modal-backdrop").
  SAFE_CODE_CLASS_PATTERN = /^language-[a-z0-9_-]+$/i;

  FORBID_TAGS = ["script", "style", "iframe", "object", "embed"];

  // Security-review correction (ST143-TI-17771): a FORBID_ATTR entry must be a literal attribute-name string —
  // DOMPurify's addToSet() uses each entry as an object key, so a RegExp like /^on/ silently stringifies to the
  // literal key "/^on/" and never matches a real attribute name. It was never actually forbidding anything.
  // No FORBID_ATTR is needed here: ALLOWED_ATTR_RICH_TEXT above is a strict allowlist that never includes any
  // on* attribute in the first place, so on* handlers are already excluded by construction, not by a denylist.
  // Verified: <img src=x onerror=alert(1)> -> <img src="x">.

  // Security-review finding (ST143-TI-17771, MUST FIX): `style` is allowed in ALLOWED_ATTR_RICH_TEXT to preserve
  // TinyMCE formatting (colour/alignment/size), but DOMPurify has no CSS parser and passes style VALUES through
  // untouched. A crafted style ("position:fixed;top:0;left:0;width:100vw;height:100vh;background:url(...)")
  // turns any Tier (c) sink (chat messages, questionnaire answers, notepad) into a full-viewport overlay over the
  // app — a UI-redress/credential-phishing vector, plus a CSS-level tracking beacon via background:url().
  //
  // What this does NOT close, by design, and must not be mistaken for closed: `img`/`src` are allowlisted, so
  // `<img src="https://evil.tld/beacon.png">` is a silent cross-origin beacon (IP, User-Agent, read-receipt
  // timing) with no interaction required — the CSS url() filter below has zero bearing on this path. Accepted
  // as the cost of allowing images in rich text at all; not something a stricter style/attribute allowlist can
  // fix. If this needs closing, it requires a CSP img-src restriction, server-side image proxying, or dropping
  // img/src from the profile used by the highest-risk sink specifically — tracked as a follow-up, not silently
  // assumed covered by the style fix below.
  //
  // A first version of this fix rejected style VALUES matching a regex denylist
  // (/position\s*:|url\s*\(|expression\s*\(|@import|-moz-binding/i). Security re-review found and empirically
  // reproduced (in real Chromium) a bypass: CSS identifiers accept escapes, so `\70 osition:fixed` never
  // literally contains the substring "position" and sails past the regex while the browser still parses it as
  // `position: fixed`; `image-set("https://evil.tld/x" 1x)` is a `url()`-free beacon with the same effect.
  // A denylist over the raw string can never close this — the browser's CSS tokenizer normalizes far more than
  // any regex can enumerate.
  //
  // Fixed properly below: assign the value to a real CSSStyleDeclaration (node.style.cssText), which forces the
  // browser's own parser to resolve escapes/shorthands, then rebuild the attribute from only an ALLOWLIST of
  // known-safe longhand properties (never `position`, offsets, `transform`, `z-index`, `pointer-events`,
  // `opacity` — the properties that make an overlay possible), with a remaining `url(`/`image-set(` check on
  // the surviving values as defense in depth for any future allowlist addition. Fails safe under a test DOM
  // (happy-dom) that does not fully resolve CSS escapes: an unresolved property simply never enumerates and
  // is dropped, same outcome as a value the parser rejects outright.
  // Note (kept accurate after later widening, MR !1586 review): `margin*` and `width`/`height` are no longer
  // universally excluded — `width`/`height` are plain attributes (not style, and non-executable) allowed on
  // every tag, and `margin*` is allowed on `<img>` only with an additional non-negative-value restriction
  // (see IMG_ONLY_SAFE_STYLE_PROPERTIES / IMG_SAFE_MARGIN_VALUE_PATTERN below) after a negative-margin overlay
  // bypass was found and closed on that scoped widening.
  // Security re-review finding (MUST FIX): a real browser's CSSStyleDeclaration only ever enumerates LONGHAND
  // properties — assigning the shorthand "border:1px solid red" or "text-decoration:underline" to cssText and
  // reading it back yields "border-top-width"/"border-top-style"/... / "text-decoration-line"/... , never the
  // shorthand name itself. Listing the shorthands here (as an earlier version of this fix did) meant they were
  // never matched in any real browser — dead allowlist entries that silently stripped every inline border and
  // underline TinyMCE's table plugin writes, while only *appearing* to allow them (happy-dom, used in tests,
  // wrongly enumerates the shorthand form, which is why this needs a comment and not just relying on the test
  // suite). Listed here as the actual longhands Chromium produces. Deliberately NOT allowlisting
  // border-image-source: unlike the other border longhands, it accepts url()/image-set() values directly.
  SAFE_STYLE_PROPERTIES = [
    "color",
    "background-color",
    "font-family",
    "font-size",
    "font-weight",
    "font-style",
    "font-variant",
    "text-align",
    // Engines disagree on whether text-decoration is expanded: real Chromium enumerates it as the four
    // longhands below when assigned as a shorthand; the happy-dom engine used by this project's test suite
    // keeps the shorthand name itself. The value is safe either way (never accepts a URL), so both forms are
    // allowlisted rather than relying on one engine's expansion behavior.
    "text-decoration",
    "text-decoration-line",
    "text-decoration-style",
    "text-decoration-color",
    "text-decoration-thickness",
    "text-transform",
    "line-height",
    "list-style-type",
    "border-top-width",
    "border-right-width",
    "border-bottom-width",
    "border-left-width",
    "border-top-style",
    "border-right-style",
    "border-bottom-style",
    "border-left-style",
    "border-top-color",
    "border-right-color",
    "border-bottom-color",
    "border-left-color",
    "border-top-left-radius",
    "border-top-right-radius",
    "border-bottom-left-radius",
    "border-bottom-right-radius",
    "border-collapse",
    "border-spacing",
    "padding",
    "padding-top",
    "padding-right",
    "padding-bottom",
    "padding-left",
    "vertical-align",
    "white-space",
  ];

  // MR review finding (ST143-TI-17771, MR !1586, Thibault Chardigny): margin*/float/display were excluded from
  // SAFE_STYLE_PROPERTIES above specifically to close the full-viewport overlay bypass. TinyMCE's `image`
  // plugin uses exactly these three to centre an image (`margin: 0 auto`, `display: block`) or wrap text
  // around it (`float: left/right`) — existing centred/wrapped images silently lost that alignment. Scoped
  // fix: allow them ONLY on `<img>` (enforced in the afterSanitizeAttributes hook below, not globally).
  // `display` is restricted to the 3 layout keywords TinyMCE actually emits (never `none`, which would let
  // rich text silently hide arbitrary sibling content it doesn't own).
  //
  // Security re-review finding (BLOCKING): scoping `margin*` to `<img>` by TAG was not enough — a NEGATIVE
  // margin (e.g. `margin-top:-400px;margin-left:-200px`) combined with a large `width`/`height` (already
  // allowed) lets the image visually escape its flow position and cover unrelated sibling content, reproduced
  // in real Chromium against the app's own chat container CSS (elementFromPoint over earlier messages/buttons
  // resolved to the attacker's <img> after this). The "no position, so no overlay" reasoning above holds only
  // for non-negative margins — negative margins are effectively a positioning primitive within the flow.
  // Fixed by additionally restricting the VALUE, the same way `display` already is: only non-negative lengths
  // or `auto` survive.
  IMG_ONLY_SAFE_STYLE_PROPERTIES = [
    "margin",
    "margin-top",
    "margin-right",
    "margin-bottom",
    "margin-left",
    "float",
    "display",
  ];
  IMG_SAFE_DISPLAY_VALUES = ["block", "inline-block", "inline"];
  // Matches "auto", "0", or a non-negative number with an optional CSS length/percentage unit — rejects any
  // negative value in any unit (px, em, %, vh, ...) and rejects calc()/min()/max()/clamp() wrappers entirely
  // (their serialized form cannot be range-checked as a plain number by this regex, so they are excluded
  // rather than risk missing a negative-producing expression).
  IMG_SAFE_MARGIN_VALUE_PATTERN = /^(auto|0|\d+(\.\d+)?(px|em|rem|%|pt|pc|in|cm|mm|ex|ch|vw|vh|vmin|vmax)?)$/i;

  // Security re-review finding (MUST FIX): var()/env()/attr() are not substituted at cssText-assignment time,
  // so a value like "background-color:var(--evil)" (with --evil defined elsewhere, e.g. by an ancestor the
  // sanitized content does not control but the surrounding page does) passes this regex unresolved today and
  // would silently defeat it the moment any URL-accepting property is ever added to SAFE_STYLE_PROPERTIES
  // above. Not exploitable today (custom properties are never allowlisted, so any --xxx definition is itself
  // stripped), but the whole point of this pattern is to hold as a future safety net — added defensively.
  UNSAFE_STYLE_VALUE_PATTERN = /url\(|image-set\(|var\(|env\(|attr\(/i;

  /**
   * Rebuilds a `style` attribute value keeping only allowlisted, non-positioning properties, after letting the
   * browser's own CSS parser resolve escapes/shorthands (see SAFE_STYLE_PROPERTIES above for why a regex over
   * the raw string is not sufficient).
   * @param {string} styleValue
   * @param {string[]} extraSafeProperties additional properties to allow, scoped by the caller to a specific
   *   tag (see IMG_ONLY_SAFE_STYLE_PROPERTIES — never pass these unconditionally for every element)
   * @returns {string} the safe cssText, or "" if nothing survives
   */
  sanitizeStyleValue(styleValue, extraSafeProperties = []) {
    const probe = document.createElement("div");
    probe.style.cssText = styleValue;

    const safeDeclarations = [];
    for (const property of Array.from(probe.style)) {
      const value = probe.style.getPropertyValue(property);
      if (!value || this.UNSAFE_STYLE_VALUE_PATTERN.test(value)) {
        continue;
      }

      const isAllowedProperty = this.SAFE_STYLE_PROPERTIES.includes(property) || extraSafeProperties.includes(property);
      if (!isAllowedProperty) {
        continue;
      }

      // `display` additionally needs its VALUE restricted regardless of which list allowed the property name —
      // `display: none` would let rich text silently hide arbitrary sibling content it doesn't own.
      if (property === "display" && !this.IMG_SAFE_DISPLAY_VALUES.includes(value)) {
        continue;
      }

      // Same reasoning for margin*: gated on the property name itself (not on extraSafeProperties), so this
      // stays fail-safe even if `margin` is ever added to the global SAFE_STYLE_PROPERTIES list. A negative
      // value is excluded regardless of which tag the property was allowed on.
      if (property.startsWith("margin") && !this.IMG_SAFE_MARGIN_VALUE_PATTERN.test(value)) {
        continue;
      }

      safeDeclarations.push(`${property}: ${value}`);
    }

    return safeDeclarations.join("; ");
  }

  /**
   * Keeps `class` only on `<code>`, and only the token(s) matching SAFE_CODE_CLASS_PATTERN (see that field for
   * why this is enforced token-by-token rather than testing the whole attribute value).
   * @param {Element} node
   */
  sanitizeClassAttribute(node) {
    if (!node.hasAttribute("class")) {
      return;
    }

    if (node.tagName !== "CODE") {
      node.removeAttribute("class");
      return;
    }

    const safeTokens = node
      .getAttribute("class")
      .split(/\s+/)
      .filter((token) => this.SAFE_CODE_CLASS_PATTERN.test(token));

    if (safeTokens.length) {
      node.setAttribute("class", safeTokens.join(" "));
    } else {
      node.removeAttribute("class");
    }
  }

  constructor() {
    DOMPurify.addHook("afterSanitizeAttributes", (node) => {
      // Force any non-same-page target ("_blank" or a named window) to carry rel="noopener" so a sanitized
      // link can never retain window.opener access to the parent page (reverse tabnabbing). Done via a
      // DOMPurify hook (not a second innerHTML pass) so the attribute is added to the already-sanitized DOM
      // tree, not by re-parsing a string.
      if (node.tagName === "A") {
        const target = node.getAttribute("target");
        if (target && !["_self", "_parent", "_top"].includes(target)) {
          node.setAttribute("rel", "noopener");
        }
      }

      this.sanitizeClassAttribute(node);

      if (node.hasAttribute("style")) {
        const extraSafeProperties = node.tagName === "IMG" ? this.IMG_ONLY_SAFE_STYLE_PROPERTIES : [];
        const safeStyle = this.sanitizeStyleValue(node.getAttribute("style"), extraSafeProperties);
        if (safeStyle) {
          node.setAttribute("style", safeStyle);
        } else {
          node.removeAttribute("style");
        }
      }
    });
  }

  /**
   * Sanitizes an HTML string for the given profile.
   * @param {string|null|undefined} html
   * @param {string} profile one of RICH_TEXT_PROFILE / MAIL_PROFILE
   * @returns {string}
   */
  sanitize(html, profile = this.RICH_TEXT_PROFILE) {
    if (!html || typeof html !== "string") {
      return "";
    }

    const config = {
      ALLOWED_TAGS: this.ALLOWED_TAGS_RICH_TEXT,
      ALLOWED_ATTR: this.ALLOWED_ATTR_RICH_TEXT,
      FORBID_TAGS: this.FORBID_TAGS,
      // Security re-review finding (ST143-TI-17771, BLOCKING, 3rd pass): DOMPurify's ALLOW_DATA_ATTR/
      // ALLOW_ARIA_ATTR default to true and are checked BEFORE the ALLOWED_ATTR allowlist above — every
      // data-*/aria-* attribute survived sanitization regardless of what ALLOWED_ATTR_RICH_TEXT lists. The
      // app imports the full Bootstrap JS bundle (main.js), whose data-API (data-bs-toggle="modal"/
      // "offcanvas", data-bs-target, data-bs-config) turned this into a non-dismissible full-viewport
      // backdrop + scroll lock reachable from a single chat message (reproduced: a link with
      // data-bs-toggle="modal" data-bs-config='{"backdrop":"static","keyboard":false}' opens a backdrop that
      // survives Escape and backdrop clicks). This was structurally the same defect as the `class` finding
      // above: an allowlist is not exhaustive by default, and DOMPurify carves out these two attribute
      // families unconditionally unless explicitly told not to. Nothing in this codebase's TinyMCE output or
      // tests relies on data-*/aria-* surviving sanitization.
      ALLOW_DATA_ATTR: false,
      ALLOW_ARIA_ATTR: false,
    };

    if (profile === this.MAIL_PROFILE) {
      config.ALLOWED_ATTR = [...config.ALLOWED_ATTR, "width", "height", "align", "valign", "bgcolor", "border"];
    }

    return DOMPurify.sanitize(html, config);
  }

  /**
   * Strips all markup and returns plain text content. Used before truncating a rich-text value for a preview
   * (e.g. NotepadNoteComponent, FormAlertComponent) — truncating raw or sanitized HTML by character count can
   * cut inside a tag/entity and reintroduce broken markup (ST143-TI-17771 §2.6 truncation rule: strip or
   * sanitize FIRST, then truncate).
   * @param {string|null|undefined} html
   * @returns {string}
   */
  stripTags(html) {
    if (!html || typeof html !== "string") {
      return "";
    }

    // Sanitize first (removes <script>/on*/javascript: — the untrusted part), then read textContent to drop the
    // remaining (now-safe) markup. Assigning to innerHTML here is safe specifically because the string has
    // already been through DOMPurify.sanitize() on the line above, not because the input itself is trusted.
    const container = document.createElement("div");
    container.innerHTML = this.sanitize(html, this.RICH_TEXT_PROFILE);
    return container.textContent || "";
  }
}

export const htmlSanitizer = new HtmlSanitizer();
